// IndexedDB durability journal. Every MediaRecorder chunk is written here
// BEFORE it is queued for upload; rows are pruned only once the server has
// acknowledged the covering multipart part. On reload, unfinished sessions
// can be detected and their un-acked bytes re-uploaded.

const DB_NAME = 'clip-journal';
const DB_VERSION = 1;

export interface SessionRow {
  slug: string;
  container: string;
  createdAt: number;
  partSize: number;
  completed: boolean;
}

export interface ChunkRow {
  slug: string;
  seq: number;
  offset: number; // byte offset of this chunk within the recording stream
  size: number;
  bytes: ArrayBuffer;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('sessions')) {
        db.createObjectStore('sessions', { keyPath: 'slug' });
      }
      if (!db.objectStoreNames.contains('chunks')) {
        const store = db.createObjectStore('chunks', { keyPath: ['slug', 'seq'] });
        store.createIndex('bySlug', 'slug');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(db: IDBDatabase, stores: string[], mode: IDBTransactionMode, fn: (t: IDBTransaction) => IDBRequest<T> | void): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(stores, mode);
    let result: T | undefined;
    const req = fn(t);
    if (req) {
      req.onsuccess = () => (result = req.result);
    }
    t.oncomplete = () => resolve(result as T);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export class Journal {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    this.db = await openDb();
  }

  private need(): IDBDatabase {
    if (!this.db) throw new Error('journal not initialized');
    return this.db;
  }

  async createSession(row: SessionRow): Promise<void> {
    await tx(this.need(), ['sessions'], 'readwrite', (t) => t.objectStore('sessions').put(row));
  }

  async putChunk(row: ChunkRow): Promise<void> {
    await tx(this.need(), ['chunks'], 'readwrite', (t) => t.objectStore('chunks').put(row));
  }

  /** Delete chunks entirely covered by acknowledged bytes [0, byteEnd). */
  async pruneThrough(slug: string, byteEnd: number): Promise<void> {
    const db = this.need();
    const rows = await this.chunksFor(slug);
    await tx(db, ['chunks'], 'readwrite', (t) => {
      const store = t.objectStore('chunks');
      for (const r of rows) {
        if (r.offset + r.size <= byteEnd) store.delete([r.slug, r.seq]);
      }
    });
  }

  async chunksFor(slug: string): Promise<ChunkRow[]> {
    const rows = await tx<ChunkRow[]>(this.need(), ['chunks'], 'readonly', (t) =>
      t.objectStore('chunks').index('bySlug').getAll(slug),
    );
    return (rows ?? []).sort((a, b) => a.seq - b.seq);
  }

  async listUnfinishedSessions(): Promise<SessionRow[]> {
    const rows = await tx<SessionRow[]>(this.need(), ['sessions'], 'readonly', (t) =>
      t.objectStore('sessions').getAll(),
    );
    return (rows ?? []).filter((s) => !s.completed);
  }

  async finishSession(slug: string): Promise<void> {
    const db = this.need();
    const rows = await this.chunksFor(slug);
    await tx(db, ['sessions', 'chunks'], 'readwrite', (t) => {
      t.objectStore('sessions').delete(slug);
      const store = t.objectStore('chunks');
      for (const r of rows) store.delete([r.slug, r.seq]);
    });
  }
}
